import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, Loader2, Trash2, Upload } from "lucide-react";
import { api, downloadPdf, errMsg, fmtDate } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/common";

export const VetFilesDialog = ({ vet, onOpenChange }) => {
  const [files, setFiles] = useState([]);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inputKey, setInputKey] = useState(0);

  const load = useCallback(() => {
    if (vet) api.get(`/admin/vets/${vet.id}/files`).then((r) => setFiles(r.data));
  }, [vet]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) return toast.error("Scegli un file PDF");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    setBusy(true);
    try {
      await api.post(`/admin/vets/${vet.id}/files`, fd);
      toast.success("File caricato");
      setTitle(""); setFile(null); setInputKey((k) => k + 1);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const del = async (f) => { if (window.confirm(`Eliminare "${f.title}"?`)) { await api.delete(`/admin/files/${f.id}`); load(); } };
  const dl = (f) => downloadPdf(`/files/${f.id}/download`, f.original_filename).catch((e) => toast.error(errMsg(e)));

  return (
    <Dialog open={!!vet} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="vet-files-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0D3B52]">File PDF · {vet?.clinic_name}</DialogTitle>
          <DialogDescription>I file caricati qui compaiono nell'area riservata del veterinario.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 rounded-xl border border-[#E2DDD5] p-4 bg-[#FAF8F5]">
          <Field label="Titolo (facoltativo)"><Input value={title} onChange={(e) => setTitle(e.target.value)} className="bg-white" data-testid="vet-file-title" /></Field>
          <Field label="File PDF (max 15 MB)">
            <Input key={inputKey} type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="bg-white" data-testid="vet-file-input" />
          </Field>
          <Button onClick={upload} disabled={busy} data-testid="vet-file-upload" className="rounded-full bg-[#0D3B52] hover:bg-[#082535]">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}Carica
          </Button>
        </div>
        <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="vet-files-list">
          {files.length === 0 ? <p className="text-sm text-[#5C6B73]">Nessun file caricato.</p> : files.map((f) => (
            <div key={f.id} className="flex items-center gap-2 rounded-xl border border-[#E2DDD5] p-3">
              <div className="flex-1 min-w-0"><p className="font-semibold text-sm text-[#0D3B52] truncate">{f.title}</p><p className="text-xs text-[#5C6B73]">{fmtDate(f.created_at?.slice(0, 10))} · {(f.size / 1024).toFixed(0)} KB</p></div>
              <Button size="icon" variant="ghost" onClick={() => dl(f)} data-testid={`vet-file-download-${f.id}`}><FileDown className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => del(f)} data-testid={`vet-file-delete-${f.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
